import { useEffect, useState } from 'react';
import type { StainDto } from '../api/companions';
import { petBasePath, type PetBaseVariant } from '../compositor/assetPaths';
import { markPet3dUnavailable, usePet3d } from './loader';
import { createSnapshotCache, stainsKey } from './snapshotCache';
import type { Outfit, Pet3dModule } from './types';

let cache = createSnapshotCache(60, (u) => URL.revokeObjectURL?.(u));
let inflight = new Map<string, Promise<string>>();
let loggedSnapshotFailure = false;
const NO_STAINS: StainDto[] = [];
const NO_OUTFIT: Outfit = {};

/** dress.js's outfitKey, copied: this file is in the initial bundle and cannot import the chunk (a test holds the two equal). */
export const outfitKey = (outfit: Outfit | null | undefined, { scene = true }: { scene?: boolean } = {}): string =>
  Object.entries(outfit ?? {})
    .filter(([s]) => scene || (s !== 'backdrop' && s !== 'foreground'))
    .map(([s, w]) => `${s}:${w.itemCode}:${w.dyeHex ?? ''}`).sort().join(',');

/**
 * Test-only: isolate the snapshot cache/inflight map and the log-once flag across cases (mirrors
 * resetMissingPetBases in assetPaths.ts).
 */
export function resetPetImageCacheForTest(): void {
  cache = createSnapshotCache(60, (u) => URL.revokeObjectURL?.(u));
  inflight = new Map();
  loggedSnapshotFailure = false;
}

/** A pet picture's src: the 2D PNG now, the 3D snapshot once it exists (PNG on any failure). */
export function usePetImage(
  species: string, stage: string, variant: PetBaseVariant = 'base', stains: StainDto[] = NO_STAINS, outfit: Outfit = NO_OUTFIT, enabled = true,
): string {
  return useSnapshotSrc(
    `${species}|${stage}|${variant}|${stainsKey(stains)}|${outfitKey(outfit)}`,
    petBasePath(species, stage, variant),
    (mod) => mod.snapshot({ species, stage, variant, stains, outfit }),
    enabled,
  );
}

/**
 * The shared body of every snapshot picture (pets, enemies): the PNG first, then the cached 3D
 * snapshot for `key`; `run` renders it on a miss. One cache, one in-flight map, one log-once flag.
 */
export function useSnapshotSrc(key: string, png: string, run: (mod: Pet3dModule) => Promise<string>, enabled = true): string {
  const mod = usePet3d();
  // { key, src }, not a bare src: when species/stage/variant/stains change, the render below must
  // show the new PNG immediately rather than the previous picture's cached/snapshot src for one
  // paint while the effect catches up (spec-required; see fix round 1, item 3).
  const [state, setState] = useState(() => ({ key, src: (enabled ? cache.get(key) : undefined) ?? png }));
  useEffect(() => {
    const hit = enabled ? cache.get(key) : undefined;   // a disabled picture is the PNG, cached 3D or not
    if (hit) { setState({ key, src: hit }); return; }
    setState({ key, src: png });
    if (!mod || !enabled) return;
    let live = true;
    let job = inflight.get(key);
    if (!job) {
      // async wrapper: a run() that throws synchronously must reject, not crash the screen (3D never breaks one)
      job = (async () => run(mod))().then((url) => { cache.set(key, url); return url; });
      inflight.set(key, job);
      job.finally(() => inflight.delete(key)).catch(() => {});
    }
    job.then(
      (url) => { if (live) setState({ key, src: url }); },
      (err: unknown) => {
        // Logged once per session (module-level flag), not once per picture -- a lost context or
        // a permanently unavailable renderer would otherwise warn once per pet on screen.
        if (!loggedSnapshotFailure) {
          loggedSnapshotFailure = true;
          console.warn('pet3d snapshot failed', err);
        }
        // snapshot.js throws this code for a lost-and-unrebuildable context (spec §3.2/§8): every
        // surface falls back to its PNG for the rest of the session, not just this one picture.
        if (err && typeof err === 'object' && (err as { code?: unknown }).code === 'pet3d_unavailable') {
          markPet3dUnavailable();
        }
      },
    );
    return () => { live = false; };
    // key covers species/stage/variant/stains
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, mod, png, enabled]);
  // Until the effect catches up with a new key, a cached snapshot still beats one PNG paint.
  if (!enabled) return png;
  return state.key === key ? state.src : cache.get(key) ?? png;
}
